// ---------------------------------------------------------------------------
// CapsuleBar -- Atomic Capsule Bar Primitive
// ---------------------------------------------------------------------------
// A horizontal bar with rounded-full ends (capsule motif), featuring:
//   - Track (background) + Fill (foreground)
//   - 3 sizes: sm (4px), md (10px), lg (20px)
//   - Animated fill with configurable stagger delay
//   - Color from the 8-color Felton palette
// ---------------------------------------------------------------------------

import React from 'react';
import { getChartColorCss } from '../constants/chartColors';
import { CHART_ANIMATION } from '../constants/chartAnimation';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type CapsuleSize = 'sm' | 'md' | 'lg';

export interface CapsuleBarProps {
  /** Current value (fill amount) */
  readonly value: number;
  /** Maximum value (denominator for bar width calculation) */
  readonly maxValue: number;
  /** Total value (denominator for percentage calculation). If not provided, maxValue is used. */
  readonly totalValue?: number;
  /** Index into the Felton chart palette (wraps via modulo) */
  readonly colorIndex: number;
  /** Explicit color override (CSS value). When provided, takes priority over colorIndex. */
  readonly colorOverride?: string;
  /** Capsule height variant */
  readonly size?: CapsuleSize;
  /** Optional label displayed to the left of the bar */
  readonly label?: string;
  /** Show the numeric value to the right of the bar */
  readonly showValue?: boolean;
  /** Stagger delay in ms for cascade animation */
  readonly animationDelay?: number;
  /** Additional CSS class on the outer wrapper */
  readonly className?: string;
}

// ---------------------------------------------------------------------------
// Size class mapping
// ---------------------------------------------------------------------------

const SIZE_CLASSES: Readonly<Record<CapsuleSize, string>> = Object.freeze({
  sm: 'h-[3px]',
  md: 'h-[5px]',
  lg: 'h-[14px]',
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const CapsuleBar: React.FC<CapsuleBarProps> = ({
  value,
  maxValue,
  totalValue,
  colorIndex,
  colorOverride,
  size = 'md',
  label,
  showValue = false,
  animationDelay = 0,
  className = '',
}) => {
  // Bar width is based on maxValue (largest item)
  const safeMax = maxValue > 0 ? maxValue : 1;
  const rawWidthPercent = (value / safeMax) * 100;
  const widthPercent = Math.max(0, Math.min(100, rawWidthPercent));

  // Display percentage is based on totalValue (sum of all items) if provided
  const safeTotal = totalValue && totalValue > 0 ? totalValue : safeMax;
  const rawDisplayPercent = (value / safeTotal) * 100;
  const percent = Math.max(0, Math.min(100, rawDisplayPercent));

  const fillColor = colorOverride ?? getChartColorCss(colorIndex);
  const sizeClass = SIZE_CLASSES[size];

  const transitionStyle: React.CSSProperties = {
    width: `${widthPercent}%`,
    backgroundColor: fillColor,
    transition: `width ${CHART_ANIMATION.duration}ms ${CHART_ANIMATION.easing} ${animationDelay}ms`,
  };

  return (
    <div className={`space-y-1 ${className}`}>
      {/* Header row: Label (left) + Value/Percentage (right) */}
      {(label || showValue) && (
        <div className="flex items-center justify-between gap-2">
          {label && (
            <span className="text-xs font-medium text-v11-text-secondary truncate">{label}</span>
          )}

          {showValue && (
            <div className="flex-shrink-0 text-right font-sans text-xs tabular-nums">
              <span className="font-bold text-v11-text-primary">
                {value.toLocaleString('en-US')}
              </span>{' '}
              <span className="font-medium text-v11-text-muted">({percent.toFixed(0)}%)</span>
            </div>
          )}
        </div>
      )}

      {/* Bar row: Full width capsule bar */}
      <div
        role="progressbar"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={maxValue}
        aria-label={label}
      >
        <div
          data-testid="capsule-track"
          className={`w-full rounded-full bg-v11-bg-secondary/40 ${sizeClass}`}
        >
          <div
            data-testid="capsule-fill"
            className={`rounded-full ${sizeClass}`}
            style={transitionStyle}
          />
        </div>
      </div>
    </div>
  );
};
